import { ClientDataType } from "@/api/ClientApi";
import { ClientNameFields } from "@utils/ClientLabel";
import { CREDIT_NOTE_DISCOUNT_TYPE_CODE, DISCOUNT_CODES, ON_INVOICE_DISCOUNT_TYPE_CODE } from "@utils/Constants";

/** Campos de snapshot de cliente persistidos en el documento (factura/cotización). */
export const DOCUMENT_CLIENT_SNAPSHOT_KEYS = [
  "clientId",
  "nombre_factura",
  "razon_social",
  "tipo_identificacion",
  "identificacion",
  "tel1",
  "tel1_cd",
  "tel2",
  "tel2_cd",
  "email",
  "direccion",
  "clientActivitycode",
  "plazo",
  "tipo",
  "einvoice_kind",
  "with_user_info",
] as const;

/**
 * Los dos nombres que un documento guarda del cliente:
 * - `nombre_factura` es el que se ve en pantalla, listados e impresion; cuando el cliente
 *   tiene nombre comercial, ese gana.
 * - `razon_social` conserva el nombre legal, que es el que Hacienda espera en
 *   `Receptor/Nombre`; el nombre comercial viaja aparte en `Receptor/NombreComercial`.
 */
export function resolveDocumentClientNames(client: ClientNameFields): {
  nombre_factura: string;
  razon_social: string;
} {
  const nombre = `${client.nombre || ""}`.trim();
  const nombreComercial = `${client.nombre_comercial || ""}`.trim();

  return {
    nombre_factura: nombreComercial || nombre,
    razon_social: nombre,
  };
}

export type DocumentClientSnapshotKey = (typeof DOCUMENT_CLIENT_SNAPSHOT_KEYS)[number];

/**
 * Valores de formulario al copiar un documento: snapshot del documento origen
 * más client_code auxiliar del maestro (no se guarda en el documento).
 */
export function buildDocumentCopyFormValues<T extends Record<string, unknown>>(
  formData: T,
  masterClient?: ClientDataType | null
): T & { client_code?: string } {
  return {
    ...formData,
    client_code: masterClient?.codigo,
  };
}

/**
 * Campos de descuento/exoneración que un documento persiste pero que en el formulario
 * viven en el slice `eDocument`, no en un input: `resetFormValue` no los restaura.
 */
export type StoredDocumentDiscount = {
  porcenta_descuento?: number | null;
  discountCode?: string | null;
  descuento_con_nota?: boolean | null;
  exenta?: boolean | null;
};

export type ResolvedDocumentDiscount = {
  documentDiscount: number;
  documentDiscountType: string;
  documentDiscountCode?: keyof typeof DISCOUNT_CODES;
  isDocumentExempted: boolean;
};

/**
 * Traduce los campos de descuento guardados en un documento (factura/cotización) al
 * estado que espera el slice `eDocument`. Es pura a propósito: cada formulario despacha
 * el resultado, siempre **antes** de `loadProducts`, porque `setDocumentDiscount`
 * sobrescribe el `porcentaje_descuento` de cada línea y `setDocumentDiscountType`
 * decide si el impuesto se calcula sobre el subtotal con o sin descuento.
 */
export function resolveStoredDocumentDiscount(
  document: StoredDocumentDiscount
): ResolvedDocumentDiscount {
  let documentDiscount = Number(document.porcenta_descuento || 0);
  if (isNaN(documentDiscount)) {
    documentDiscount = 0;
  }

  return {
    documentDiscount,
    documentDiscountType: document.descuento_con_nota
      ? CREDIT_NOTE_DISCOUNT_TYPE_CODE
      : ON_INVOICE_DISCOUNT_TYPE_CODE,
    documentDiscountCode: document.discountCode
      ? (`${document.discountCode}` as keyof typeof DISCOUNT_CODES)
      : undefined,
    isDocumentExempted: !!document.exenta,
  };
}

export type PersistableDocumentDiscount = {
  /** El descuento es en factura y hay monto, pero no se eligió código: el form debe abortar. */
  requiresDiscountCode: boolean;
  /** Valor a persistir: el código elegido, o `null` para limpiar el que estuviera guardado. */
  discountCode: string | null;
};

/**
 * Inversa de `resolveStoredDocumentDiscount`: decide qué `discountCode` guardar a partir del
 * estado del slice `eDocument`. El código solo aplica al descuento en factura con monto; en
 * cualquier otro caso devuelve `null` para limpiar el que hubiera quedado de una edición previa
 * (bajar el descuento a 0 o pasarlo a nota de crédito no debe dejar el código huérfano).
 */
export function resolveDocumentDiscountToPersist(
  documentDiscountType: string | undefined,
  descuento: number,
  documentDiscountCode?: string
): PersistableDocumentDiscount {
  const isOnInvoiceDiscount = documentDiscountType === ON_INVOICE_DISCOUNT_TYPE_CODE;

  if (!isOnInvoiceDiscount || !(descuento > 0)) {
    return { requiresDiscountCode: false, discountCode: null };
  }

  if (!documentDiscountCode) {
    return { requiresDiscountCode: true, discountCode: null };
  }

  return { requiresDiscountCode: false, discountCode: documentDiscountCode };
}

/** Evita que onInputChange(clientId) sobrescriba el snapshot durante resetFormValue. */
export function endDocumentPreload(isPreloadingRef: { current: boolean }): void {
  queueMicrotask(() => {
    isPreloadingRef.current = false;
  });
}
